'use client';

import { FC, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { Content, isFilled, asLink } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

// react-pdf is a client-only library (uses Canvas / Web APIs and pdfjs worker).
// Loading it via next/dynamic with ssr:false prevents the module from being
// evaluated during the server build, which avoids path-resolution errors in
// Vercel's Linux build environment.
const PdfViewer = dynamic(
  () => import('./PdfViewer').then((mod) => mod.PdfViewer),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-[640px] items-center justify-center bg-white text-[#031a14]">
        Loading PDF viewer…
      </div>
    ),
  },
);

const PhaseCheckIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.935 1.63324C12.8174 0.689959 11.1826 0.689961 10.065 1.63324L9.04719 2.49231C8.57218 2.89323 7.98438 3.13672 7.365 3.1891L6.03786 3.30133C4.58061 3.42457 3.42457 4.58061 3.30133 6.03786L3.1891 7.365C3.13672 7.98438 2.89323 8.57218 2.49231 9.0472L1.63324 10.065C0.689959 11.1826 0.689961 12.8174 1.63324 13.935L2.49231 14.9528C2.89323 15.4278 3.13672 16.0156 3.1891 16.635L3.30133 17.9622C3.42457 19.4194 4.58061 20.5755 6.03786 20.6986L7.365 20.8108C7.98438 20.8633 8.57218 21.1068 9.0472 21.5077L10.065 22.3668C11.1826 23.31 12.8174 23.31 13.935 22.3668L14.9528 21.5077C15.4278 21.1068 16.0156 20.8633 16.635 20.8108L17.9622 20.6986C19.4194 20.5755 20.5755 19.4194 20.6986 17.9622L20.8108 16.635C20.8633 16.0156 21.1068 15.4278 21.5077 14.9528L22.3668 13.935C23.31 12.8174 23.31 11.1826 22.3668 10.065L21.5077 9.04719C21.1068 8.57218 20.8633 7.98438 20.8108 7.365L20.6986 6.03786C20.5755 4.58061 19.4194 3.42457 17.9622 3.30133L16.635 3.1891C16.0156 3.13672 15.4278 2.89323 14.9528 2.49231L13.935 1.63324ZM17.4552 9.95466C17.9824 9.42745 17.9824 8.57268 17.4552 8.04547C16.9281 7.51825 16.0732 7.51825 15.546 8.04547L10.5006 13.0909L8.45524 11.0455C7.92804 10.5183 7.07326 10.5183 6.54606 11.0455C6.01884 11.5727 6.01884 12.4275 6.54606 12.9547L9.54606 15.9546C10.0733 16.4818 10.928 16.4818 11.4552 15.9546L17.4552 9.95466Z"
      fill="#00F557"
    />
  </svg>
);

/**
 * Props for `EbookContentsHighlight`.
 */
export type EbookContentsHighlightProps =
  SliceComponentProps<Content.EbookContentsHighlightSlice>;

// ── PdfPreviewWithCtas variation ────────────────────────────────────────────

const PdfPreviewWithCtas: FC<{
  slice: Content.EbookContentsHighlightSlicePdfPreviewWithCtas;
}> = ({ slice }) => {
  const {
    eyebrow,
    title,
    meta,
    preview_image,
    preview_pdf,
    open_fullscreen_button,
    download_button,
    background_pattern,
  } = slice.primary;

  // Prismic Link to Media resolves to an object with .url and .name
  const pdfUrl =
    preview_pdf && 'url' in preview_pdf
      ? (preview_pdf.url as string | undefined)
      : undefined;
  const pdfName =
    preview_pdf && 'name' in preview_pdf
      ? (preview_pdf.name as string | undefined)
      : undefined;

  return (
    <section
      data-slice-type="ebook_contents_highlight"
      data-slice-variation="pdfPreviewWithCtas"
      className="py-12 text-white md:py-16"
    >
      <div className="relative mx-[30px] overflow-hidden rounded-[24px] border border-[#00f557]/30 bg-[#031a14] shadow-[0_0_60px_rgba(0,245,87,0.08)]">
        {isFilled.image(background_pattern) && (
          <PrismicNextImage
            field={background_pattern}
            fill
            className="pointer-events-none absolute inset-0 object-cover opacity-50"
            fallbackAlt=""
          />
        )}

        <div className="relative z-10 mx-auto flex flex-col items-center gap-8 px-6 py-16 md:px-10 md:py-20">
          {/* Eyebrow pill */}
          {eyebrow && (
            <div className="inline-flex rounded-full border border-[#00f557] px-5 py-2">
              <span className="text-[14px] font-semibold uppercase leading-[1.2] tracking-wide text-white">
                {eyebrow}
              </span>
            </div>
          )}

          {/* Title */}
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading1: ({ children }) => (
                  <h2 className="max-w-[920px] text-center text-[32px] font-bold leading-[1.15] md:text-[48px]">
                    {children}
                  </h2>
                ),
                heading2: ({ children }) => (
                  <h2 className="max-w-[920px] text-center text-[32px] font-bold leading-[1.15] md:text-[48px]">
                    {children}
                  </h2>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold text-[#00f557]">
                    {children}
                  </strong>
                ),
              }}
            />
          )}

          {/* Meta line */}
          {meta && (
            <p className="text-center text-[15px] font-medium text-white/85 md:text-[16px]">
              {meta}
            </p>
          )}

          {/* PDF preview embed — Prismic Media link to a PDF rendered with the
            browser's built-in PDF viewer (toolbar + scroll come from the
            browser). */}
          {pdfUrl ? (
            <div className="w-full max-w-[1000px] overflow-hidden rounded-xl shadow-[0_8px_48px_rgba(0,0,0,0.35)]">
              <PdfViewer url={pdfUrl} fileName={pdfName} />
            </div>
          ) : isFilled.image(preview_image) ? (
            <div className="w-full max-w-[1000px] overflow-hidden rounded-xl shadow-[0_8px_48px_rgba(0,0,0,0.35)]">
              <PrismicNextImage
                field={preview_image}
                className="block h-auto w-full"
                fallbackAlt=""
              />
            </div>
          ) : null}

          {/* CTA buttons */}
          {(isFilled.link(open_fullscreen_button) ||
            isFilled.link(download_button)) && (
            <div className="flex flex-col items-center gap-4 sm:flex-row">
              {isFilled.link(open_fullscreen_button) &&
                (() => {
                  // The PDFs live as static files under /public/Resources, so a
                  // link like /Resources/The-Cost-of-Standing-Still.pdf is a real
                  // asset — but navigating to it with next/link makes the client
                  // router treat the path as a React route, find none, and render
                  // the 404 page. A native <a> does a full browser navigation, so
                  // Next serves the static PDF instead of client-routing. Opening
                  // in a new tab also avoids the stale-page-on-back issue that
                  // client-routing to a non-app URL causes.
                  const fullscreenUrl = asLink(open_fullscreen_button) ?? '';
                  return (
                    <a
                      href={fullscreenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full border border-white/60 px-7 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-white/10 md:text-[15px]"
                    >
                      {open_fullscreen_button.text || 'Open full screen'}
                    </a>
                  );
                })()}
              {isFilled.link(download_button) &&
                (() => {
                  // Prismic's LinkField.url is typed as string | undefined,
                  // so coalesce to keep dlUrl a plain string downstream.
                  const dlUrl =
                    'url' in download_button ? (download_button.url ?? '') : '';
                  // Prismic's Link to Media field exposes the original upload
                  // filename via `.name`. Pass it through so the proxy saves
                  // the file with that name instead of Prismic CDN's hashed slug.
                  const dlName =
                    'name' in download_button &&
                    typeof download_button.name === 'string' &&
                    download_button.name
                      ? download_button.name
                      : '';
                  // Route downloadable files through /api/download so
                  // Content-Disposition: attachment is enforced even for
                  // cross-origin sources like Prismic's Media CDN.
                  const isDownloadable = /\.(pdf|zip|docx?|xlsx?)$/i.test(
                    dlUrl,
                  );
                  const href = isDownloadable
                    ? `/api/download?url=${encodeURIComponent(dlUrl)}${
                        dlName ? `&name=${encodeURIComponent(dlName)}` : ''
                      }`
                    : dlUrl;
                  return (
                    <a
                      href={href}
                      className="inline-flex items-center justify-center rounded-full bg-[#00f557] px-7 py-3 text-[14px] font-bold text-[#002726] transition-opacity hover:opacity-90 md:text-[15px]"
                    >
                      {download_button.text || 'Download PDF'}
                    </a>
                  );
                })()}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ── Default variation (animated phases) ────────────────────────────────────

const DefaultVariation: FC<{
  slice: Content.EbookContentsHighlightSliceDefault;
}> = ({ slice }) => {
  const { heading, description, phases, supporting_image } = slice.primary;

  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const phaseRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('opacity-0', 'translate-y-8');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (contentRef.current) {
      contentRef.current.classList.add(
        'opacity-0',
        'translate-y-8',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      observer.observe(contentRef.current);
    }

    if (imageRef.current) {
      imageRef.current.classList.add(
        'opacity-0',
        'translate-y-8',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      imageRef.current.style.transitionDelay = '200ms';
      observer.observe(imageRef.current);
    }

    phaseRefs.current.forEach((phase, index) => {
      if (phase) {
        phase.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        phase.style.transitionDelay = `${300 + index * 150}ms`;
        observer.observe(phase);
      }
    });

    return () => observer.disconnect();
  }, [phases]);

  return (
    <section
      data-slice-type="ebook_contents_highlight"
      data-slice-variation={slice.variation}
      className="w-full py-16 md:py-[80px]"
    >
      <div className="mx-auto max-w-[1380px] px-5 md:px-[100px]">
        <div className="overflow-hidden rounded-[24px] bg-[#041118] px-8 py-12 md:px-16 md:py-16">
          <div className="flex flex-col gap-10 md:flex-row md:gap-16">
            {/* Left: heading + description + phases */}
            <div ref={contentRef} className="flex flex-1 flex-col gap-8">
              {isFilled.richText(heading) && (
                <div className="font-inter text-[28px] font-medium leading-[1.2] text-white md:text-[38px] [&_h2]:text-[28px] [&_h2]:font-medium md:[&_h2]:text-[38px]">
                  <PrismicRichText field={heading} />
                </div>
              )}

              {isFilled.richText(description) && (
                <div className="font-inter text-[15px] font-normal leading-[1.6] text-white/80 md:text-[16px]">
                  <PrismicRichText field={description} />
                </div>
              )}

              {/* Phase list */}
              {phases && phases.length > 0 && (
                <div className="flex flex-col gap-6">
                  {phases.map((phase, idx) => (
                    <div
                      key={idx}
                      ref={(el) => {
                        phaseRefs.current[idx] = el;
                      }}
                      className="flex flex-col gap-2"
                    >
                      <div className="flex items-center gap-3">
                        <PhaseCheckIcon />
                        {isFilled.richText(phase.phase_title) && (
                          <div className="font-inter text-[18px] font-semibold leading-[1.4] text-[#00F557] md:text-[20px] [&_strong]:font-semibold">
                            <PrismicRichText field={phase.phase_title} />
                          </div>
                        )}
                      </div>
                      {isFilled.richText(phase.phase_description) && (
                        <div className="pl-9 font-inter text-[14px] font-normal leading-[1.6] text-white/80 md:text-[16px]">
                          <PrismicRichText field={phase.phase_description} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: supporting image */}
            {isFilled.image(supporting_image) && (
              <div
                ref={imageRef}
                className="flex shrink-0 items-center justify-center md:w-[400px]"
              >
                <PrismicNextImage
                  field={supporting_image}
                  className="h-auto w-full object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Main component (dispatches by variation) ────────────────────────────────

const EbookContentsHighlight: FC<EbookContentsHighlightProps> = ({ slice }) => {
  if (slice.variation === 'pdfPreviewWithCtas') {
    return <PdfPreviewWithCtas slice={slice} />;
  }
  return <DefaultVariation slice={slice} />;
};

export default EbookContentsHighlight;
